// AuthStatus Component
// This component displays the current authentication status and provides login/logout functionality

import React, { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { User, LogOut, ChevronDown, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useAppDispatch } from '../../store';
import { requestNavigateMainTab } from '../../store/slices/modalsSlice';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';

export const AuthStatus: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user, loading, isFirebaseAvailable, logout } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState<{ email: string; verificationSent: boolean } | null>(null);
  const [registerInitialError, setRegisterInitialError] = useState<string | null>(null);

  const handleSwitchToRegister = (initialError?: string) => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
    if (initialError) setRegisterInitialError(initialError);
  };

  const handleSwitchToForgotPassword = () => {
    // For now, just close login modal - forgot password modal can be added later
    setShowLoginModal(false);
  };

  const handleSwitchToLogin = (info?: { email: string; verificationSent: boolean }) => {
    setShowRegisterModal(false);
    if (info) {
      setVerificationInfo(info);
      setShowLoginModal(true);
    } else {
      setVerificationInfo(null);
      setShowLoginModal(true);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (!isFirebaseAvailable) {
    // When Firebase is not configured, useAuth sets a dev user. Show label + visible Sign out button.
    if (user) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Use Tailwind text-green-500 so the [data-theme="light"] CSS rule in
              design-system.css darkens it to green-700 on white surfaces, while
              dark mode keeps the brighter #22c55e (4.7:1 on #0e131e). */}
          <span
            className="text-green-500"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              fontSize: '0.625rem',
              fontWeight: '500',
            }}
          >
            <i className="fas fa-user-check" style={{ fontSize: '0.5rem' }}></i>
            <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.displayName?.split(' ')[0] || user.email?.split('@')[0]}
            </span>
            <span style={{ opacity: 0.8 }}>(Dev)</span>
          </span>
          {/* text-red-500 default = #ef4444 (4.6:1 on #0e131e dark, AA);
              [data-theme="light"] CSS rule darkens to red-700 (#b91c1c, 6.5:1 on white). */}
          <button
            type="button"
            onClick={handleLogout}
            className="text-red-500"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              fontSize: '0.625rem',
              fontWeight: '500',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title={t('common.signOut', { defaultValue: 'Sign out' })}
          >
            <LogOut style={{ fontSize: '0.625rem' }} />
            {t('common.signOut', { defaultValue: 'Sign out' })}
          </button>
        </div>
      );
    }
    return (
      <div style={{
        padding: '4px 8px',
        fontSize: '0.6875rem',
        backgroundColor: 'var(--tv-warning-bg, rgba(255, 193, 7, 0.1))',
        color: 'var(--tv-warning-text, #ff9800)',
        borderRadius: '4px',
        border: '1px solid var(--tv-warning-border, rgba(255, 193, 7, 0.2))'
      }}>
        <i className="fas fa-exclamation-triangle" style={{ marginRight: '4px', fontSize: '0.5rem' }}></i>
        No Auth
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        padding: '6px 10px',
        fontSize: '0.75rem',
        color: 'var(--tv-text-secondary)',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: '0.625rem' }}></i>
        Loading
      </div>
    );
  }

  if (user) {
    const userName = user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'User';

    return (
      <>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: '44px',
                padding: '4px 8px',
                backgroundColor: 'var(--tv-success-bg, rgba(76, 175, 80, 0.1))',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.625rem',
                color: '#15803d', /* green-700 - passes WCAG AA on both light (#fff) and dark (#0e131e) backgrounds */
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              title="Click to sign out"
            >
              <User style={{ fontSize: '0.625rem' }} />
              <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userName}
              </span>
              <ChevronDown style={{ fontSize: '0.5rem', opacity: 0.7 }} />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={4}
              style={{
                backgroundColor: 'var(--tv-card-bg, #1e222d)',
                border: '1px solid var(--tv-border-color, #363a45)',
                borderRadius: '8px',
                padding: '4px',
                minWidth: '140px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                zIndex: 1000,
              }}
            >
              <DropdownMenu.Item
                onSelect={() => dispatch(requestNavigateMainTab('settings'))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  outline: 'none',
                  color: 'var(--text-primary, #dee2f2)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--surface_container_high, rgba(255,255,255,0.06))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <UserCog style={{ fontSize: '0.75rem' }} />
                {t('common.settings', { defaultValue: 'Settings' })}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={handleLogout}
                className="text-red-500"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  outline: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <LogOut style={{ fontSize: '0.75rem' }} />
                {t('common.signOut', { defaultValue: 'Sign Out' })}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          onClick={() => setShowLoginModal(true)}
          style={{
            padding: '4px 8px',
            fontSize: '0.625rem',
            fontWeight: '500',
            border: 'none',
            backgroundColor: 'transparent',
            color: 'var(--accent-primary)',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--tv-primary-bg, rgba(33, 150, 243, 0.1))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Sign In
        </button>
        <span style={{ color: 'var(--tv-text-secondary)', fontSize: '0.625rem' }}>|</span>
        <button
          onClick={() => setShowRegisterModal(true)}
          style={{
            padding: '4px 8px',
            fontSize: '0.625rem',
            fontWeight: '500',
            border: 'none',
            backgroundColor: 'transparent',
            color: 'var(--tv-text-secondary)',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--tv-bg-secondary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Sign Up
        </button>
      </div>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setVerificationInfo(null);
        }}
        onSwitchToRegister={handleSwitchToRegister}
        onSwitchToForgotPassword={handleSwitchToForgotPassword}
        {...(verificationInfo ? {
          verificationEmail: verificationInfo.email,
          showVerificationState: true,
          verificationSent: verificationInfo.verificationSent
        } : {})}
      />

      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => {
          setShowRegisterModal(false);
          setRegisterInitialError(null);
        }}
        onSwitchToLogin={handleSwitchToLogin}
        initialError={registerInitialError}
      />
    </>
  );
};

export default AuthStatus;